import {
  useController,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import { ChangeEvent, KeyboardEvent, useCallback, useId } from "react";
import { formatExpiry } from "../../../utils/formatExpiry";

interface IFormExpirationDateInputProps {
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  "data-testid"?: string;
}

export { formatExpiry };

export default function FormExpirationDateInput<
  FormValues extends FieldValues = FieldValues
>({
  label,
  placeholder,
  isRequired,
  isDisabled,
  "data-testid": testId,
  ...controllerProps
}: UseControllerProps<FormValues> & IFormExpirationDateInputProps) {
  const {
    field,
    fieldState: { error },
    formState: { isSubmitting },
  } = useController(controllerProps);

  const inputId = useId();
  const isFieldDisabled = isSubmitting || isDisabled;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      field.onChange(formatExpiry(e.target.value));
    },
    [field]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && field.value?.endsWith("/")) {
        e.preventDefault();
        field.onChange(field.value.slice(0, -1));
      }
    },
    [field]
  );

  return (
    <span className="payment-expiry-field inline-flex flex-col w-full relative">
      {label && (
        <label
          htmlFor={inputId}
          className={`inline-block pb-1 text-xs ${
            error ? "payment-error" : ""
          }`}
        >
          {label}
          {isRequired ? "*" : ""}
        </label>
      )}
      <input
        {...field}
        id={inputId}
        aria-invalid={error != null}
        aria-required={isRequired}
        aria-describedby={error == null ? undefined : `${inputId}-error`}
        value={formatExpiry(field.value ?? "")}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={isFieldDisabled}
        placeholder={placeholder ?? "MM/YYYY"}
        inputMode="numeric"
        autoComplete="cc-exp"
        maxLength={7}
        data-testid={testId}
        className={[
          "bg-white border px-3 py-2 rounded outline-0 min-h-10 w-full",
          isFieldDisabled ? "text-grey-40" : "",
          error ? "payment-invalid-border" : "border-grey-60",
        ]
          .filter(Boolean)
          .join(" ")}
      />
      <div
        id={`${inputId}-error`}
        hidden={error == null}
        role={error == null ? undefined : "alert"}
        className={`pt-2 text-sm ${error ? "payment-error" : "text-grey-25"}`}
      >
        {error?.message ?? <span>&nbsp;</span>}
      </div>
    </span>
  );
}
